import React from "react";
import { CustomerDropdown } from "../CustomerDropdown";
import { Customer } from "../shared/interfaces/Customer";
import { useTranslation } from "react-i18next";
import { CompanyDropdown } from "../CompanyDropdown";
import { SiteDropdown } from "../SiteDropdown";
import { InputText } from "primereact/inputtext";
import { Card } from "../shared/Card";
import { FormContainer } from "../shared/FormLayout/FormContainer";
import { FormRow } from "../shared/FormLayout/FormRow";
import { ValidationMessage } from "../shared/ValidationMessage";
import { UserRolesDropdown } from "./UserRolesDropdown";
import { UserRoles } from "./UserRolesEnum";
import { useAlternativeRegionNames } from "../data/Settings/useAlternativeRegionNames";
import { Region } from "../shared/interfaces/Region";
import { RegionType } from "../shared/enums/Region";

interface UserManageProps {
  title: string;
  customer?: Customer;
  company?: Region;
  site?: Region;
  userRole?: UserRoles;
  email?: string;
  isValid?: boolean;
  onCustomerSelect: (customer: Customer | undefined) => void;
  onCompanySelect: (company: Region | undefined) => void;
  onSiteSelect: (site: Region | undefined) => void;
  onUserRoleSelect: (userRole: UserRoles | undefined) => void;
  onEmailSelect: (email: string) => void;
  isCustomerValid: boolean;
  isUserRoleValid: boolean;
  isEmailValid: boolean;
  customerId?: number;
}

export function UserManage(props: UserManageProps) {
  const { t } = useTranslation();

  const {
    customer,
    company,
    site,
    userRole,
    email,
    onCustomerSelect,
    onCompanySelect,
    onUserRoleSelect,
    onEmailSelect,
    onSiteSelect,
    isCustomerValid,
    isUserRoleValid,
    isEmailValid,
    customerId,
  } = props;

  const [singleCustomer, setSingleCustomer] = React.useState<boolean>(false);

  const altRegionNames = useAlternativeRegionNames(customer?.id);

  function handleCustomerSelect(customer: Customer | undefined) {
    if (customer) {
      onCustomerSelect(customer);
      onCompanySelect(undefined);
      onSiteSelect(undefined);
    }
  }

  function handleSingleCustomer(customer: Customer) {
    onCustomerSelect(customer);
    onCompanySelect(undefined);
    onSiteSelect(undefined);
    setSingleCustomer(true);
  }

  function handleCompanyChange(companyName: string | undefined, companyId: number | undefined) {
    if (companyName && companyId && customer && customer.id) {
      onCompanySelect({
        name: companyName,
        id: companyId,
        customerId: customer.id,
        regionType: RegionType.Company,
        geofenceId: 0,
      });
      onSiteSelect(undefined);
    }
  }

  function handleSiteChange(siteName: string | undefined, siteId: number | undefined) {
    if (siteName && siteId && customer && customer.id && company && company.id)
      onSiteSelect({
        name: siteName,
        id: siteId,
        customerId: customer.id,
        regionType: RegionType.Company,
        geofenceId: 0,
      });
  }

  function handleEmailInput(event: React.FormEvent<HTMLInputElement>) {
    onEmailSelect(event.currentTarget.value);
  }

  function handleUserRoleSelect(userRole: UserRoles | undefined) {
    if (userRole) {
      onUserRoleSelect(userRole);
    }
    onCompanySelect(undefined);
    onSiteSelect(undefined);
  }

  function isUserAdminRole(userRole: UserRoles | undefined) {
    return userRole === UserRoles.SuperAdmin || userRole === UserRoles.AdminUser;
  }

  return (
    <Card title={props.title} className="mb-3">
      <FormContainer>
        <FormRow
          label={t("login.email")}
          value={
            <div>
              <InputText
                value={email}
                onChange={handleEmailInput}
                className={!isEmailValid ? "p-invalid" : undefined}
              />
              {!isEmailValid && (
                <ValidationMessage
                  invalidMessage={t<string>("common.pleaseEnter", {
                    val: t("login.email"),
                  })}
                  validationType="error"
                  showIcon
                />
              )}
            </div>
          }
        />

        <FormRow
          label={t("userManagement.userRole")}
          value={
            <div>
              <UserRolesDropdown
                onSelectUserRole={handleUserRoleSelect}
                selectedUserRole={userRole}
                className={!isUserRoleValid ? "p-invalid" : undefined}
              />
              {!isUserRoleValid && (
                <ValidationMessage
                  invalidMessage={t<string>("common.pleaseEnter", {
                    val: t("userManagement.userRole"),
                  })}
                  validationType="error"
                  showIcon
                />
              )}
            </div>
          }
        />

        {!singleCustomer && (
          <FormRow
            label={t("customer.customer")}
            value={
              <div>
                <CustomerDropdown
                  onCustomerSelect={handleCustomerSelect}
                  onSingleCustomerAvailable={handleSingleCustomer}
                  selectedCustomer={customer}
                  updateLocalStorage={false}
                  className={!isCustomerValid ? "p-invalid" : undefined}
                  customerId={customerId}
                  filterUserManagement={true}
                />
                {!isCustomerValid && (
                  <ValidationMessage
                    invalidMessage={t<string>("common.pleaseEnter", {
                      val: t("customer.customer"),
                    })}
                    validationType="error"
                    showIcon
                  />
                )}
              </div>
            }
          />
        )}

        {customer && customer.id && !isUserAdminRole(userRole) && (
          <FormRow
            label={altRegionNames.data?.companyAltName ?? t("common.company")}
            value={
              <CompanyDropdown
                customerId={customer?.id}
                selectedCompanyName={company?.name ?? ""}
                onCompanyChange={handleCompanyChange}
                clearAuto
              />
            }
          />
        )}

        {customer && customer.id && company && company.name && !isUserAdminRole(userRole) && (
          <FormRow
            label={altRegionNames.data?.siteAltName ?? t("common.site")}
            value={
              <SiteDropdown
                onSiteChange={handleSiteChange}
                customerId={customer.id}
                companyId={company.id}
                selectedSiteName={site?.name ?? ""}
                clearAuto
              />
            }
          />
        )}
      </FormContainer>
    </Card>
  );
}
